import { Link } from 'react-router';
import { SearchX } from 'lucide-react';
import { useCityLabel } from '@/i18n/use-city-label';
import { useLocale, useTranslations } from '@/i18n/use-locale';
import { useMemo, useState } from 'react';
import { DataTable, useTableSort, type DataTableColumn } from '@/components/admin/data-table';
import { FilterChips } from '@/components/admin/filter-chips';
import { AdminPagination } from '@/components/admin/pagination';
import { EmptyState } from '@/components/ui/empty-state';
import { StatusBadge } from '@/components/ui/status-badge';
import { publicProfileUrl } from '@/lib/public-site';
import { formatCount } from '@/lib/format';
import { clampPage, paginate } from '@/lib/table';
import { verificationTone } from '@/lib/status';
import { adminInitials, type TalentSearchHit } from '@/contracts';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const PER_PAGE = 8;

export type TalentsFilter = 'all' | 'verified' | 'pending';

/**
 * The photo when there is one, the monogram when there is not. The same shape and
 * size either way, so a column of rows with and without photos stays aligned, and
 * the same initials rule the admin avatars use — the definite article is skipped so
 * two Al-Something surnames do not share a letter.
 */
function TalentAvatar({
  name,
  photoUrl,
  alt,
}: {
  name: string;
  photoUrl?: string | null;
  alt: string;
}) {
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={alt}
        className="h-9 w-9 shrink-0 rounded-full border border-conc-100 object-cover"
      />
    );
  }
  return (
    <span
      aria-hidden
      data-slot="monogram"
      className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-navy text-[11px] font-bold text-white"
    >
      {adminInitials(name)}
    </span>
  );
}

/**
 * The talents list on the overview screen — and the place the console's table, filter
 * and pagination primitives are proven against real API rows rather than fixtures.
 *
 * Filter, then sort, then page: reversing the last two would sort a single page against
 * itself. All three run client-side over rows the server already fetched, which is
 * honest at C8's scale and wrong at Phase 2. When `/v1/talents/search` starts returning
 * one page at a time, `rows` becomes that page, `total` arrives as a prop, and the state
 * below moves into the query string. The components do not change.
 */
export function TalentsTable({
  rows,
  specialtyNames,
  onDelete,
  onRemovePhoto,
  detailHref,
  detailState,
  onOpen,
  filter: controlledFilter,
  onFilterChange,
}: {
  rows: readonly TalentSearchHit[];
  /**
   * Id to label. The API sends the id — every client resolves it against the
   * catalogue, which the console already fetches for its own sidebar — and this
   * table renders whichever name the locale asks for (ADR-013).
   */
  specialtyNames: Readonly<Record<string, string>>;
  /**
   * When supplied, the actions cell gains a delete control. The dashboard's
   * overview omits it — reading is not deleting — and the candidates screen
   * passes one. The confirmation and the reason live with the caller, so this
   * component stays a table.
   */
  onDelete?: (row: TalentSearchHit) => void;
  /**
   * When supplied, a row that carries a photo gains a "remove photo" control. Only
   * offered where the photo is — a row showing the monogram has nothing to take
   * down. The reason and the confirmation live with the caller, as for `onDelete`.
   */
  onRemovePhoto?: (row: TalentSearchHit) => void;
  /**
   * When supplied, the name links to the talent's console detail screen instead of the
   * public profile (which keeps its own action in the last column), carrying
   * `detailState` so the detail's back link can restore this list's filter.
   */
  detailHref?: (row: TalentSearchHit) => string;
  detailState?: unknown;
  /** A click anywhere on the row that is not one of its own links or buttons. */
  onOpen?: (row: TalentSearchHit) => void;
  /** Controlled status chip — the candidates screen keeps it in the query string. */
  filter?: TalentsFilter;
  onFilterChange?: (filter: TalentsFilter) => void;
}) {
  const t = useTranslations('admin');
  const common = useTranslations('common');
  const cityLabel = useCityLabel();
  const { locale } = useLocale();

  const [localFilter, setLocalFilter] = useState<TalentsFilter>('all');
  const filter = controlledFilter ?? localFilter;
  const setFilter = onFilterChange ?? setLocalFilter;
  const [page, setPage] = useState(1);

  const columns: ReadonlyArray<DataTableColumn<TalentSearchHit>> = useMemo(
    () => [
      {
        key: 'name',
        header: t('table.talent'),
        sortValue: (row) => row.fullName,
        cell: (row) => (
          <span className="flex items-center gap-3">
            <TalentAvatar
              name={row.fullName}
              photoUrl={row.photoUrl}
              alt={t('talents.photo.alt', { name: row.fullName })}
            />
            <span className="min-w-0">
              <Link
                to={detailHref ? detailHref(row) : publicProfileUrl(row.slug)}
                state={detailHref ? detailState : undefined}
                className="font-semibold text-navy transition hover:text-pitch hover:underline"
              >
                {row.fullName}
              </Link>
              {row.headline && <p className="mt-0.5 text-[11.5px] text-conc-500">{row.headline}</p>}
            </span>
          </span>
        ),
      },
      {
        key: 'specialty',
        header: t('table.specialty'),
        sortValue: (row) => specialtyNames[row.specialtyId] ?? row.specialtyId,
        cell: (row) => (
          <span className="text-conc-600">
            {specialtyNames[row.specialtyId] ?? row.specialtyId}
          </span>
        ),
      },
      {
        key: 'years',
        header: t('table.experience'),
        sortValue: (row) => row.yearsOfExperience,
        cellClassName: 'num text-conc-600',
        cell: (row) => t('table.yearsValue', { count: row.yearsOfExperience }),
      },
      {
        key: 'city',
        header: t('table.city'),
        sortValue: (row) => cityLabel(row.city),
        cell: (row) => <span className="text-conc-600">{cityLabel(row.city) ?? '—'}</span>,
      },
      {
        key: 'status',
        header: t('table.status'),
        sortValue: (row) => (row.verified ? 1 : 0),
        cell: (row) => (
          <StatusBadge tone={verificationTone(row.verified ? 'verified' : 'pending')}>
            {row.verified ? t('status.verified') : t('status.pending')}
          </StatusBadge>
        ),
      },
      {
        key: 'actions',
        header: <span className="sr-only">{common('actions')}</span>,
        cellClassName: 'text-end',
        cell: (row) => (
          <span className="inline-flex items-center gap-3">
            <Link
              to={publicProfileUrl(row.slug)}
              className="text-[12.5px] font-semibold text-pitch hover:underline"
            >
              {common('openProfile')}
            </Link>
            {onRemovePhoto && row.photoUrl && (
              <Button
                type="button"
                variant="link"
                size="none"
                onClick={() => onRemovePhoto(row)}
                className="text-[12.5px] text-conc-600 hover:text-navy"
              >
                {t('talents.photo.remove')}
              </Button>
            )}
            {onDelete && (
              <Button
                type="button"
                variant="link"
                size="none"
                onClick={() => onDelete(row)}
                className="text-[12.5px] text-rose-600"
              >
                {common('delete')}
              </Button>
            )}
          </span>
        ),
      },
    ],
    [t, common, cityLabel, specialtyNames, onDelete, onRemovePhoto, detailHref, detailState],
  );

  const filtered = useMemo(() => {
    if (filter === 'all') return rows;
    return rows.filter((row) => (filter === 'verified' ? row.verified : !row.verified));
  }, [rows, filter]);

  const { sort, toggle, sorted } = useTableSort(
    filtered,
    columns,
    { key: 'name', direction: 'asc' },
    locale,
  );

  const safePage = clampPage(page, sorted.length, PER_PAGE);
  const visible = paginate(sorted, safePage, PER_PAGE);

  return (
    <div className="space-y-4">
      <FilterChips<TalentsFilter>
        label={t('filters.statusLabel')}
        value={filter}
        onChange={(next) => {
          setFilter(next);
          setPage(1);
        }}
        options={[
          { value: 'all', label: t('filters.all'), count: rows.length },
          {
            value: 'verified',
            label: t('status.verified'),
            count: rows.filter((row) => row.verified).length,
          },
          {
            value: 'pending',
            label: t('status.pending'),
            count: rows.filter((row) => !row.verified).length,
          },
        ]}
      />

      <Card className="overflow-hidden">
        <DataTable
          rows={visible}
          columns={columns}
          rowKey={(row) => row.id}
          onRowClick={onOpen}
          sort={sort}
          onSortToggle={toggle}
          caption={t('table.caption')}
          empty={
            <EmptyState icon={SearchX} title={t('table.emptyTitle')} hint={t('table.emptyHint')} />
          }
          footer={
            <AdminPagination
              page={safePage}
              perPage={PER_PAGE}
              total={sorted.length}
              onPageChange={setPage}
              summary={t('pagination.summary', {
                shown: formatCount(visible.length),
                total: formatCount(sorted.length),
              })}
              previousLabel={t('pagination.previous')}
              nextLabel={t('pagination.next')}
              pageLabel={(value) => t('pagination.page', { page: formatCount(value) })}
            />
          }
        />
      </Card>
    </div>
  );
}
